import {
  DEFAULT_LOCALE,
  LOCALES,
  LOCALE_TO_HTML_LANG,
  isLocale,
  type Locale,
} from './locales.js';

/**
 * Diccionario de slugs traducidos por locale. Fuente de verdad
 * única — la web (sitemap, helpers de URL, selector de idioma) y
 * cualquier consumidor futuro lo importan de aquí.
 *
 * Para el `home` el slug es vacío: la URL coincide con
 * `/<locale>`. Para el resto, cada locale tiene un slug propio.
 *
 * Convenciones:
 * - `event` / `artist` son **detalle** (esperan un `param`
 *   adicional, p. ej. `/es/evento/rosalia-madrid`).
 * - `events` / `artists` / `store` son **listados**.
 *
 * Para añadir una nueva ruta o un idioma: actualizar este
 * diccionario; el typecheck obligará a rellenar todas las celdas.
 */
export const ROUTE_SLUGS = {
  home: { es: '', ca: '', en: '' },
  events: { es: 'eventos', ca: 'esdeveniments', en: 'events' },
  // Detalle (`/<locale>/<slug>/<eventoSlug>`). En EN reutilizamos el
  // mismo `events` que el listado: Next distingue por la presencia
  // del segmento adicional `[slug]`. ES y CA mantienen singular.
  event: { es: 'evento', ca: 'esdeveniment', en: 'events' },
  artists: { es: 'artistas', ca: 'artistes', en: 'artists' },
  artist: { es: 'artista', ca: 'artista', en: 'artist' },
  // Detalle de colección (`/<locale>/coleccion|colleccio|collection/<slug>`).
  collection: { es: 'coleccion', ca: 'colleccio', en: 'collection' },
  store: { es: 'tienda', ca: 'botiga', en: 'store' },
  blog: { es: 'blog', ca: 'blog', en: 'blog' },
  // Páginas legales: solo en castellano, servidas con el mismo slug
  // (= nombre de carpeta) en los tres locales, así que no requieren
  // rewrite en el middleware.
  terms: {
    es: 'terminos-y-condiciones',
    ca: 'terminos-y-condiciones',
    en: 'terminos-y-condiciones',
  },
  privacy: {
    es: 'politica-de-privacidad',
    ca: 'politica-de-privacidad',
    en: 'politica-de-privacidad',
  },
} as const satisfies Record<string, Record<Locale, string>>;

export type RouteKey = keyof typeof ROUTE_SLUGS;

export interface LocalizedHrefOptions {
  /** Slug paramétrico añadido al final (p. ej. el slug de un evento concreto). */
  param?: string;
  /** Querystring sin el `?` inicial. */
  query?: string;
  /** Hash sin el `#` inicial. */
  hash?: string;
}

/**
 * Construye un href absoluto-relativo (`/es/eventos/...`) para
 * una ruta lógica en el locale indicado. Es la forma canónica de
 * construir enlaces internos: nunca hardcodees `/event` ni
 * `/events` en componentes — pasa por aquí.
 */
export function localizedHref(
  key: RouteKey,
  locale: Locale,
  options: LocalizedHrefOptions = {},
): string {
  const slug = ROUTE_SLUGS[key][locale];
  const segments: string[] = [locale];
  if (slug) segments.push(slug);
  if (options.param) segments.push(options.param);

  let path = `/${segments.join('/')}`;
  if (options.query) path += `?${options.query}`;
  if (options.hash) path += `#${options.hash}`;
  return path;
}

export interface ResolvedRoute {
  routeKey: RouteKey;
  locale: Locale;
  /** Sólo presente en rutas de detalle. */
  param?: string;
}

/**
 * Inverso de `localizedHref`: dado un pathname, devuelve a qué
 * RouteKey + locale corresponde. Lo usa el selector de idioma
 * para construir la URL equivalente en otro locale conservando
 * la página actual.
 *
 * Devuelve `null` si el pathname no encaja en el diccionario
 * (p. ej. una ruta sin prefijo de locale, o un slug desconocido).
 */
/**
 * Clasificación de RouteKey por forma de URL: si lleva o no segmento
 * adicional. Necesario para desambiguar locales donde listado y
 * detalle comparten el primer segmento (p. ej. en EN `events` se usa
 * para `events` y `event`); el `param` decide a qué key resuelve.
 */
const KEY_KIND: Record<RouteKey, 'home' | 'list' | 'detail'> = {
  home: 'home',
  events: 'list',
  event: 'detail',
  artists: 'list',
  artist: 'detail',
  collection: 'detail',
  store: 'list',
  blog: 'list',
  terms: 'list',
  privacy: 'list',
};

export function resolveRoute(pathname: string): ResolvedRoute | null {
  // Se ignora querystring/hash si llegan dentro del pathname.
  const noQuery = pathname.split('?')[0].split('#')[0];
  const cleaned = noQuery.replace(/^\/+|\/+$/g, '');
  if (!cleaned) return null;

  const segments = cleaned.split('/');
  const [maybeLocale, maybeSlug, maybeParam] = segments;
  if (!isLocale(maybeLocale)) return null;

  if (!maybeSlug) {
    return { routeKey: 'home', locale: maybeLocale };
  }

  const wantedKind: 'list' | 'detail' = maybeParam ? 'detail' : 'list';

  for (const key of Object.keys(ROUTE_SLUGS) as RouteKey[]) {
    if (KEY_KIND[key] !== wantedKind) continue;
    if (ROUTE_SLUGS[key][maybeLocale] === maybeSlug) {
      return maybeParam
        ? { routeKey: key, locale: maybeLocale, param: maybeParam }
        : { routeKey: key, locale: maybeLocale };
    }
  }
  return null;
}

export interface LanguageAlternates {
  /** URL canónica de la página actual en el locale activo. */
  canonical: string;
  /**
   * Mapa con códigos hreflang (`es-ES`, `ca`, `en`) más
   * `x-default`, listo para `Metadata.alternates.languages` de
   * Next. `metadataBase` se encarga de absolutizar las URLs.
   */
  languages: Record<string, string>;
}

/**
 * Construye `canonical` + `alternates.languages` con códigos
 * hreflang `es-ES`/`ca`/`en` + `x-default` (→ locale por
 * defecto) a partir de un builder de path por locale. El
 * consumidor decide cómo traducir la URL en cada idioma:
 *
 * Página traducida con slug por idioma:
 *   buildLanguageAlternates('ca', l => localizedHref('events', l))
 *
 * Página todavía sin traducir (mismo path en los 3 locales):
 *   buildLanguageAlternates('ca', l => `/${l}/events`)
 *
 * El día que la página se traduce, basta cambiar el builder
 * (la firma del helper no se toca).
 */
export function buildLanguageAlternates(
  currentLocale: Locale,
  pathPerLocale: (locale: Locale) => string,
): LanguageAlternates {
  const languages: Record<string, string> = {};
  for (const l of LOCALES) {
    languages[LOCALE_TO_HTML_LANG[l]] = pathPerLocale(l);
  }
  languages['x-default'] = pathPerLocale(DEFAULT_LOCALE);
  return { canonical: pathPerLocale(currentLocale), languages };
}
